/**
 * Hanro — Notify Me (Klaviyo back-in-stock) custom element (ECSR-4).
 *
 * Tracks the selected variant via Horizon's `variant:update` event and keeps
 * the `data-oos` attribute current (a sibling <style> in the block swaps the
 * buy buttons for this element while data-oos is set). Submitting the form
 * creates a Klaviyo back-in-stock subscription through the client API; the
 * live "Back In Stock - Standard" flow emails the shopper on restock.
 *
 * NOTE: on the PDP, Horizon's variant change morphs only <variant-picker>
 * itself — this block's DOM persists — so all UI state (CTA/form/success)
 * is reset from the variant:update handler, never assumed re-rendered.
 * The size <select> options are server-rendered; JS only syncs selection.
 */

const KLAVIYO_CLIENT_REVISION = '2024-10-15';

if (!customElements.get('hanro-notify-me')) {
  class HanroNotifyMe extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', this.#handleClick);
      this.addEventListener('submit', this.#handleSubmit);
      document.addEventListener('variant:update', this.#handleVariantUpdate);
    }

    disconnectedCallback() {
      document.removeEventListener('variant:update', this.#handleVariantUpdate);
    }

    #ref(name) {
      return this.querySelector(`[ref="${name}"]`);
    }

    /**
     * Returns the block to its initial state: CTA visible, form hidden and
     * re-enabled, live regions cleared. Runs on every variant change so a
     * success message for variant A never lingers for variant B.
     */
    #resetUi() {
      const cta = this.#ref('notifyCta');
      const form = this.#ref('notifyForm');
      const success = this.#ref('successMessage');
      const error = this.#ref('errorMessage');
      if (cta) cta.hidden = false;
      if (form) {
        form.hidden = true;
        const submit = form.querySelector('[type="submit"]');
        if (submit) submit.disabled = false;
        const email = form.querySelector('input[name="email"]');
        if (email) {
          email.removeAttribute('aria-invalid');
          email.removeAttribute('aria-describedby');
        }
      }
      if (success) success.textContent = '';
      if (error) error.textContent = '';
    }

    #handleVariantUpdate = (event) => {
      const productId = event.detail?.data?.productId;
      if (productId && String(productId) !== String(this.dataset.productId)) return;

      const variant = event.detail?.resource;
      if (!variant || !variant.id) {
        // Nonexistent option combination: there is no variant to subscribe
        // to. Clear data-oos so the native "Unavailable" buy button shows
        // instead of a stale Notify Me pointing at the previous variant.
        delete this.dataset.oos;
        this.#resetUi();
        return;
      }

      this.dataset.variantId = variant.id;
      if (variant.available) {
        delete this.dataset.oos;
      } else {
        this.dataset.oos = 'true';
      }
      this.#resetUi();

      const select = this.#ref('sizeSelect');
      if (select) select.value = String(variant.id);
    };

    #handleClick = (event) => {
      const cta = event.target.closest('[ref="notifyCta"]');
      if (!cta) return;
      const form = this.#ref('notifyForm');
      if (!form) return;
      form.hidden = false;
      cta.hidden = true;
      form.querySelector('input[name="email"]')?.focus();
    };

    #handleSubmit = async (event) => {
      event.preventDefault();
      const form = event.target;
      const errorEl = this.#ref('errorMessage');
      const emailInput = form.elements.email;
      const email = emailInput?.value.trim();
      const name = form.elements.name?.value.trim();
      const variantId = form.elements.size?.value || this.dataset.variantId;

      if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        this.#showError(errorEl, emailInput, 'Please enter a valid email address.');
        return;
      }
      this.#clearError(errorEl, emailInput);

      const submitButton = form.querySelector('[type="submit"]');
      if (submitButton) submitButton.disabled = true;

      // The BIS client endpoint accepts only the documented profile fields
      // (extra attributes like first_name 400) — the name is attached
      // best-effort through Klaviyo's onsite identify below instead.
      const payload = {
        data: {
          type: 'back-in-stock-subscription',
          attributes: {
            profile: { data: { type: 'profile', attributes: { email } } },
            channels: ['EMAIL'],
          },
          relationships: {
            variant: {
              data: {
                type: 'catalog-variant',
                id: `$shopify:::$default:::${variantId}`,
              },
            },
          },
        },
      };

      try {
        const response = await fetch(
          `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${encodeURIComponent(this.dataset.companyId)}`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              revision: KLAVIYO_CLIENT_REVISION,
            },
            body: JSON.stringify(payload),
          }
        );

        if (!response.ok) throw new Error(`Klaviyo responded ${response.status}`);

        if (name) {
          // Best-effort: attach the shopper's name to the Klaviyo profile via
          // the onsite script (loaded by klaviyo-integration.liquid). Never
          // blocks the success path.
          try {
            window.klaviyo?.identify({ $email: email, $first_name: name });
          } catch {
            /* non-critical */
          }
        }

        form.hidden = true;
        const success = this.#ref('successMessage');
        if (success) {
          success.textContent = success.dataset.successText || "You're on the list.";
          // Keep keyboard focus in context — the focused submit button was
          // just hidden with the form, which would drop focus to <body>.
          success.focus();
        }
      } catch (error) {
        console.error('Hanro notify-me subscription failed:', error);
        this.#showError(errorEl, emailInput, 'Something went wrong — please try again in a moment.');
      } finally {
        if (submitButton) submitButton.disabled = false;
      }
    };

    #showError(errorEl, emailInput, message) {
      if (errorEl) errorEl.textContent = message;
      if (emailInput) {
        emailInput.setAttribute('aria-invalid', 'true');
        if (errorEl?.id) emailInput.setAttribute('aria-describedby', errorEl.id);
        emailInput.focus();
      }
    }

    #clearError(errorEl, emailInput) {
      if (errorEl) errorEl.textContent = '';
      if (emailInput) {
        emailInput.removeAttribute('aria-invalid');
        emailInput.removeAttribute('aria-describedby');
      }
    }
  }
  customElements.define('hanro-notify-me', HanroNotifyMe);
}
